<script>
import { usePageField } from '@voix/composables/usePageField'

export default {
  name: 'GlueMobileNavigation',

  props: {
    menu: {
      type: Object,
      required: true,
    },
    taTrackNum: {
      type: Number,
      default: null,
    },
    canAccessGolf: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const pageField = usePageField()
    const eyebrow = pageField.get('eyebrow', null)
    const hideBookNow = pageField.get('hideBookNow', false)
    const specialOverride = pageField.get('specialOverride', null)
    const mobileNavigation = ref(null)

    const mobileNavBarHeight = computed(() => {
      if (mobileNavigation.value)
        return mobileNavigation.value.clientHeight

      return 0
    })

    return {
      eyebrow,
      hideBookNow,
      specialOverride,
      mobileNavigation,
      mobileNavBarHeight,
    }
  },

  data() {
    return {
      showMobileNav: false,
      scrollTop: 0,
      openNavigationItem: {},
    }
  },

  computed: {
    currentMenuLinks() {
      const links = []
      if (this.openNavigationItem && this.openNavigationItem.children) {
        this.openNavigationItem.children.map((child) => {
          if (child.type === 'link')
            links.push(child)

          child.children.map((grandchild) => {
            if (grandchild.type === 'link' || grandchild.type === 'url')
              links.push(grandchild)

            return grandchild
          })

          return child
        })
      }
      return links
    },
  },

  mounted() {
    this.scrollTop = document.documentElement.scrollTop
    window.addEventListener('scroll', () => {
      this.scrollTop = document.documentElement.scrollTop
    })

    const pageField = usePageField()
    const hideCasino = pageField.get('hideCasino', false)

    if (hideCasino.value) {
      const desktopNav = document.querySelector('#nav-casino')
      if (desktopNav)
        desktopNav.remove()

      const mobileNav = document.querySelector('#mobile-nav-casino')
      if (mobileNav)
        mobileNav.remove()

      const footerNav = document.querySelectorAll('.footer-nav-casino')
      if (footerNav) {
        footerNav.forEach((item) => {
          item.remove()
        })
      }
    }
  },

  methods: {
    toggleSubMenu(menuItem) {
      if (
        typeof this.openNavigationItem === 'undefined'
        || typeof this.openNavigationItem.children === 'undefined'
      ) {
        this.openNavigationItem = Object.assign({}, menuItem)
      }
      else {
        this.openNavigationItem = {}
      }
    },

    stringToSlug(str) {
      if (!str)
        return ''

      str = str.replace(/^\s+|\s+$/g, '') // trim
      str = str.toLowerCase()

      // remove accents, swap ñ for n, etc
      const from = 'àáäâèéëêìíïîòóöôùúüûñç·/_,:;'
      const to = 'aaaaeeeeiiiioooouuuunc------'
      for (let i = 0, l = from.length; i < l; i++)
        str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i))

      str = str
        .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
        .replace(/\s+/g, '-') // collapse whitespace and replace by -
        .replace(/-+/g, '-') // collapse dashes

      return str
    },
  },
}
</script>

<template>
  <div>
    <div class="h-[70px]" />
    <div
      id="mobile-navigation"
      ref="mobileNavigation"
      class=" inset-x-0 top-0 z-[60] transition-color duration-500 bg-white"
      :class="{
        'absolute': scrollTop < 100,
        'fixed': scrollTop > 100,
        'pt-8': !eyebrow,
      }"
    >
      <div
        v-if="eyebrow?.value"
        class="bg-glueblue-600"
      >
        <div class="big-container mx-auto relative z-30  text-white">
          <div class="py-2 px-8 text-xs font-sans2 tracking-wide text-center">
            <VoixWysiwyg :field="eyebrow" />
          </div>
        </div>
      </div>
      <div class="flex items-center">
        <div class="flex items-center w-1/2 space-x-4">
          <button class="text-center pl-8">
            <div
              v-show="!showMobileNav"
              @click="showMobileNav = true"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-width="2"
                  d="M4 2h18M4 10h18M4 18h18"
                />
              </svg>
            </div>
            <div
              v-show="showMobileNav"
              @click="showMobileNav = false"
            >
              <Icon
                class="w-6 h-6"
                name="heroicons:x-mark-20-solid"
              />
            </div>
          </button>
          <a
            href="/"
            class="px-4 text-center flex justify-center transition-colors delay-200 transition-color duration-500"
            :class="{
              'text-glueblue-700': showMobileNav,
              'text-gray-700': !showMobileNav,
            }"
          >
            <LogosAtlantisLogo style="width: 100px; height: 70px" />
          </a>
        </div>
        <div class="pr-8 w-1/2 flex items-center justify-end text-right">
          <a
            href="tel:180028526847"
            class="mr-2 transition-colors delay-200 transition-color duration-500"
            :class="{
              'text-glueblue-700': scrollTop > 0 || showMobileNav,
              'text-white': scrollTop === 0 && !showMobileNav,
            }"
          >
            <Icon
              style="width: 20px"
              name="heroicons:phone-solid"
            />
          </a>
          <BookingBookNow
            v-if="!hideBookNow.value"
            :track="taTrackNum"
            :dropdown="true"
            location="navigation"
            disableable
          >
            <button
              class="text-xs font-sans uppercase tracking-widest"
              @click="showMobileNav = false"
            >
              Book<br>
              Now
            </button>
          </BookingBookNow>
        </div>
      </div>
      <Transition name="fade">
        <ul
          v-if="menu"
          v-show="showMobileNav"
          class="bg-white text-glueblue-700 p-8 pb-32 overflow-y-scroll w-full fixed z-50 inset-0 mt-16"
        >
          <li
            v-for="topMenuItem in menu.menu_items"
            :id="`mobile-nav-${stringToSlug(topMenuItem.name)}`"
            :key="topMenuItem.id"
            class="border-b border-gray-400 font-sans text-15 uppercase tracking-widest items-center"
            :class="{ open: topMenuItem.open }"
          >
            <div
              v-if="topMenuItem.permalink !== '#login'"
              class="w-full flex justify-between items-center"
              style="padding: 19px 0 19px 0"
            >
              <div
                v-if="topMenuItem.children.length"
                @click="toggleSubMenu(topMenuItem)"
              >
                {{ topMenuItem.name }}
              </div>
              <a
                v-else
                :href="topMenuItem.permalink"
                class="text-glueblue-700"
                style="height: 15px"
                :rel="topMenuItem.rel"
                :target="topMenuItem.target"
                :onclick="`dataLayer.push({event: 'e_nav', menu: 'main menu', 'menuSelected': '${topMenuItem.name
                }'})`
                "
              >
                {{ topMenuItem.name }}
              </a>
              <div
                v-if="topMenuItem.children.length"
                class="block cursor-pointer"
                @click="toggleSubMenu(topMenuItem)"
              >
                <Icon
                  name="heroicons:chevron-down-20-solid"
                  class="transform duration-500"
                  :class="{
                    'rotate-90': topMenuItem.id !== openNavigationItem.id,
                    'rotate-180': topMenuItem.id === openNavigationItem.id,
                  }"
                  style="width: 12px; height: 15px"
                />
              </div>
            </div>
            <GolfLogin v-else>
              <a
                class="text-white"
                @click.prevent
              > {{ topMenuItem.name }} </a>
            </GolfLogin>
            <div
              class="overflow-hidden"
              :class="{ 'max-h-0': topMenuItem.id !== openNavigationItem.id }"
            >
              <ul class="py-4">
                <li class="pb-1">
                  <a
                    :href="topMenuItem.permalink"
                    style="height: 15px"
                    class="text-gray-500 tracking-wide"
                    :rel="topMenuItem.rel"
                    :target="topMenuItem.target"
                    :onclick="`dataLayer.push({event: 'e_nav', menu: 'main menu', 'menuSelected': '${topMenuItem.name
                    }'})`
                    "
                  >
                    Overview
                  </a>
                </li>
                <li
                  v-for="subMenuItem in currentMenuLinks"
                  :key="subMenuItem.id"
                  class="pb-1"
                >
                  <a
                    :href="subMenuItem.permalink"
                    :rel="topMenuItem.rel"
                    :target="subMenuItem.target"
                    class="text-gray-500 tracking-wide"
                    :onclick="`dataLayer.push({event: 'e_nav', menu: 'main menu', 'menuSelected': '${topMenuItem.name
                    } > ${subMenuItem.name
                    }'})`
                    "
                  >{{ subMenuItem.name }}</a>
                </li>
              </ul>
            </div>
          </li>
          <li class="font-sans2 pt-8">
            <ul
              class="md:hidden flex justify-start space-x-8 mx-8 mb-3 py-4 border-y border-gray-300 text-xs uppercase"
            >
              <li>
                <a
                  href="/blog"
                  class="text-gray-800 flex items-center space-x-1"
                  target="_self"
                >
                  <svg
                    width="26"
                    height="18"
                    viewBox="0 0 26 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M23.9902 1.86388C22.8386 0.712291 20.964 0.71179 19.8124 1.86388L17.7646 3.9112L14.3088 7.36696C14.1982 7.47762 14.1186 7.61581 14.0775 7.76651L12.3101 13.54L18.0866 11.7755C18.2378 11.735 18.3755 11.6549 18.4861 11.5442L21.9419 8.08846L23.9892 6.04064C25.1217 4.90807 25.1217 2.99494 23.9892 1.86288L23.9902 1.86388ZM19.3848 2.99994L22.8546 6.46973L21.9429 7.38148L18.4731 3.9117L19.3848 2.99994ZM14.45 8.2632C14.4586 8.29174 14.4666 8.32128 14.4746 8.35182C14.5256 8.54258 14.5892 8.78041 14.8756 9.06681C15.1605 9.3517 15.4019 9.42029 15.5956 9.47537C15.7689 9.52444 15.905 9.56349 16.0983 9.75626C16.2911 9.94902 16.3301 10.0857 16.3792 10.259C16.4343 10.4527 16.5029 10.694 16.7878 10.9789C17.0742 11.2653 17.312 11.3289 17.5027 11.38C17.5333 11.388 17.5628 11.396 17.5914 11.4045L15.3177 12.099L13.7546 10.5358L14.45 8.2632ZM13.5884 11.0781L14.775 12.2647L13.0656 12.7869L13.5884 11.0781ZM18.1767 11.1477C17.9649 10.986 17.7836 10.9369 17.6319 10.8963C17.4652 10.8518 17.333 10.8162 17.1418 10.625C16.9505 10.4337 16.9099 10.2955 16.8609 10.1223C16.8058 9.9285 16.7372 9.68716 16.4523 9.40227C16.1674 9.11738 15.9361 9.05179 15.7323 8.99371C15.5591 8.94464 15.4229 8.90558 15.2296 8.71282C15.0379 8.52105 15.0028 8.38937 14.9582 8.22264C14.9177 8.07093 14.8686 7.88968 14.7069 7.67789L18.1191 4.26569L21.5889 7.73547L18.1767 11.1477ZM23.6362 5.68815L23.2086 6.11574L19.7388 2.64596L20.1664 2.21837C21.1232 1.26155 22.6794 1.26205 23.6362 2.21837C24.5765 3.15866 24.5765 4.74785 23.6362 5.68815ZM18.0916 12.7739H18.5922V13.5325C18.5922 14.8127 17.5508 15.8542 16.2705 15.8542H8.59547L3.98862 19V15.8542H3.3217C2.04144 15.8542 1 14.8127 1 13.5325V4.62518C1 3.34492 2.04144 2.30348 3.3217 2.30348H17.6845V2.80417H3.3217C2.31732 2.80417 1.50069 3.6213 1.50069 4.62518V13.532C1.50069 14.5363 2.31782 15.353 3.3217 15.353H4.48931V18.0512L8.44076 15.353H16.27C17.2744 15.353 18.0911 14.5358 18.0911 13.532L18.0916 12.7739ZM14.6621 5.0738H3.25V4.57311H14.6621V5.0738ZM12.9675 7.25531H3.25V6.75462H12.967L12.9675 7.25531ZM12.1834 9.43682H3.25V8.93613H12.1829L12.1834 9.43682ZM10.8971 11.6183H3.25V11.1176H10.8971V11.6183ZM3.25 13.2981H10.8971V13.7988H3.25V13.2981Z"
                      fill="#989898"
                      stroke="#989898"
                      stroke-width="0.5"
                    />
                  </svg>
                  <span>Island Time Blog</span>
                </a>
              </li>
              <li>
                <a
                  href="/photo-gallery"
                  class="text-gray-800 flex items-center space-x-1"
                  target="_self"
                >
                  <svg
                    width="25"
                    height="18"
                    viewBox="0 0 25 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M22.1308 4.19079V15.8092C22.1308 16.3337 21.7037 16.7608 21.1792 16.7608H20.4867V16.2011H21.1792C21.3953 16.2011 21.571 16.0253 21.571 15.8092V4.19079C21.571 3.97472 21.3953 3.79894 21.1792 3.79894H4.19079C3.97472 3.79894 3.79894 3.97472 3.79894 4.19079V4.91852H3.23915V4.19079C3.23915 3.66627 3.66627 3.23915 4.19079 3.23915H21.1792C21.7037 3.23915 22.1308 3.66627 22.1308 4.19079ZM23.4183 1H6.42995C5.90543 1 5.47831 1.42712 5.47831 1.95164V2.67937H6.0381V1.95164C6.0381 1.73556 6.21387 1.55979 6.42995 1.55979H23.4183C23.6344 1.55979 23.8102 1.73556 23.8102 1.95164V13.5701C23.8102 13.7861 23.6344 13.9619 23.4183 13.9619H22.7259V14.5217H23.4183C23.9429 14.5217 24.37 14.0946 24.37 13.5701V1.95164C24.37 1.42712 23.9429 1 23.4183 1ZM14.5255 8.24366C13.5358 8.24366 12.7303 9.0492 12.7303 10.0389C12.7303 11.0286 13.5358 11.8341 14.5255 11.8341C15.5153 11.8341 16.3208 11.0286 16.3208 10.0389C16.3208 9.0492 15.5153 8.24366 14.5255 8.24366ZM11.3711 16.4205C10.1765 16.6041 8.94445 16.7933 7.83103 16.8683V17.4292C8.97804 17.3548 10.237 17.1611 11.4562 16.9736C13.8454 16.6063 16.3079 16.2279 17.8602 16.762V16.1736C16.1965 15.6793 13.8437 16.0404 11.3711 16.4205ZM19.8917 6.42995V18.0484C19.8917 18.5729 19.4646 19 18.94 19H1.95164C1.42712 19 1 18.5729 1 18.0484V6.42995C1 5.90543 1.42712 5.47831 1.95164 5.47831H18.94C19.4646 5.47831 19.8917 5.90543 19.8917 6.42995ZM19.3319 6.42995C19.3319 6.21387 19.1561 6.0381 18.94 6.0381H1.95164C1.73556 6.0381 1.55979 6.21387 1.55979 6.42995V18.4402H5.42842L5.47488 15.8635C4.56635 15.8607 3.73226 15.7667 3.01741 15.5299V14.9359C3.70595 15.2007 4.54899 15.3037 5.48496 15.3054L5.57285 10.4218L3.66341 12.3312C3.64773 12.3469 3.62702 12.3548 3.60631 12.3548C3.59903 12.3548 3.5912 12.3536 3.58392 12.3514C3.55537 12.343 3.5341 12.32 3.52738 12.2915C3.374 11.6175 3.57384 10.9245 4.06142 10.4364C4.21424 10.2835 4.39001 10.1576 4.5809 10.0624H2.65635C2.62668 10.0624 2.59981 10.0462 2.58526 10.0204C2.5707 9.99468 2.57182 9.96277 2.58749 9.93814C2.95528 9.35316 3.58728 9.00442 4.27694 9.00442C4.49301 9.00442 4.70629 9.03968 4.90894 9.10686L3.54865 7.74657C3.52794 7.72586 3.5201 7.69507 3.52794 7.66652C3.53634 7.63797 3.55929 7.6167 3.58784 7.60998C4.26182 7.4566 4.95484 7.65645 5.44298 8.14402C5.66241 8.36346 5.82419 8.62544 5.9216 8.91373C6.01844 8.6288 6.18022 8.36514 6.40133 8.14402C6.88947 7.65589 7.58249 7.4566 8.25647 7.60998C8.28502 7.6167 8.30798 7.63797 8.31637 7.66652C8.32477 7.69507 8.31637 7.7253 8.29566 7.74657L6.93649 9.10574C7.13634 9.03968 7.3485 9.00442 7.56681 9.00442C8.25703 9.00442 8.88848 9.35372 9.25626 9.93814C9.27193 9.96333 9.27305 9.99468 9.2585 10.0204C9.24394 10.0462 9.21707 10.0624 9.1874 10.0624H7.26565C7.45374 10.157 7.62839 10.2824 7.78289 10.4364C8.27103 10.9245 8.47031 11.6175 8.31693 12.2915C8.31021 12.32 8.28894 12.343 8.26039 12.3514C8.25312 12.3536 8.24528 12.3548 8.238 12.3548C8.21673 12.3548 8.19602 12.3464 8.1809 12.3312L6.26195 10.4123L6.34983 15.2813C7.73923 15.2058 9.27641 14.965 10.7901 14.7277C13.4787 14.3067 16.0381 13.9065 17.8602 14.3543V14.9343C16.1333 14.4584 13.4664 14.8755 10.8768 15.2808C9.34302 15.5209 7.78569 15.7644 6.36047 15.8394L6.40749 18.4397H18.9406C19.1567 18.4397 19.3325 18.2639 19.3325 18.0478L19.3319 6.42995Z"
                      fill="#989898"
                      stroke="#989898"
                      stroke-width="0.3"
                    />
                  </svg>
                  <span>Photo Gallery</span>
                </a>
              </li>
            </ul>
          </li>
          <li class="mt-2 font-sans2 font-light ">
            <a
              href="/travel-info"
              class="text-glueblue-700 livechat"
              target="_self"
            >Travel & Safety</a>
          </li>
          <li class="mt-2 font-sans2 font-light ">
            <a
              href="/events"
              class="text-glueblue-700"
              target="_self"
            >Events</a>
          </li>
          <li class="mt-2 font-sans2 font-light ">
            <a
              href="/gift-cards"
              class="text-glueblue-700"
              target="_self"
            >Gift Cards</a>
          </li>
          <li class="mt-2 font-sans2 font-light ">
            <a
              href="/faq"
              class="text-glueblue-700"
              target="_self"
            >FAQ's</a>
          </li>
          <li class="mt-2 font-sans2 font-light">
            <a
              href="tel:1-800-285-2684"
              class="text-glueblue-700"
              target="_self"
            >1-800-Atlantis</a>
          </li>
          <li>
            <div class="fixed bottom-0 left-0 right-0 py-4 flex justify-center px-8 bg-white">
              <a
                href="tel:180028526847"
                class="btn-ghost !text-glueblue-700 !border-glueblue-700 !w-1/2 ml-2"
                target="_self"
              >
                Call
              </a>
            </div>
          </li>
        </ul>
      </Transition>
    </div>
  </div>
</template>
